import { getSessionsColumns } from "@/modules/agent/components/TPRADashboard/constants/sessionsColumns";
import { useGetAllSessions } from "@/modules/agent/components/TPRADashboard/requests/getSessions";
import type { SessionDetails } from "@/modules/agent/components/TPRADashboard/types/sessions";
import UploadDialog from "@/modules/agent/components/upload-dialog";
import { AGENT_TYPES, UploadDialogState } from "@/modules/agent/types";
import { generateAndDownloadSessionReport } from "@/modules/agent/utils/downloadReport";
import { useGetTeamsListQuery } from "@/modules/auth/requests";
import { getAgentSession } from "@/modules/sessions/requests";
import type { AgentSessionStep, MultiVendorAgentSession } from "@/modules/sessions/types";
import { handleContinueMultiVendorAssessment } from "@/modules/sessions/use-cases/agent-session.use-case";
import { ZButton } from "@/shared/components/button";
import ZBreadCrumbs from "@/shared/components/custom/breadcrumbs";
import { LoadingSpinner } from "@/shared/components/custom/spin";
import ErrorBoundary from "@/shared/components/error-boundary";
import { ZTableDeprecrated } from "@/shared/components/table-deprecrated/TableDeprecrated";
import { ZTablePagination } from "@/shared/components/TablePagination";
import { SearchField } from "@/shared/components/TableSearch";
import { ROUTES } from "@/shared/constants/routes";
import { usePagination } from "@/shared/hooks/usePagination";
import { useSearch } from "@/shared/hooks/useSearch";
import { addNotification } from "@/shared/states/notification";
import parsePromise from "@/shared/utils/parsePromise";
import { cn } from "@nextui-org/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

export default function TPRASessionsPage() {
  const navigate = useNavigate();
  const [loadingRows, setLoadingRows] = useState<Record<string, boolean>>({});

  // Modal state
  const [uploadDialogState, setUploadDialogState] = useState<UploadDialogState>({
    isOpen: false,
  });

  // Fetch ALL sessions at once (no params, client-side filtering)
  const { data: allSessions, isLoading, isFetching, isError, error } = useGetAllSessions();

  // Fetch team members for name resolution
  const { data: teamsData, dataUpdatedAt: teamsDataUpdatedAt } = useGetTeamsListQuery({ isEnabled: true });

  // Local state to hold sessions with resolved member names
  const [sessions, setSessions] = useState<SessionDetails[]>([]);

  // Update sessions when data or teams data changes
  useEffect(() => {
    if (teamsDataUpdatedAt && allSessions) {
      // Resolve member names when teams data is available
      const updatedSessions = allSessions.map((session) => ({
        ...session,
        created_by: teamsData?.memberIdNameMapping?.[session.created_by_id] || "",
      }));
      setSessions(updatedSessions);
    } else if (allSessions) {
      // If teams data not loaded yet, use sessions as-is
      setSessions(allSessions);
    }
  }, [allSessions, teamsData?.memberIdNameMapping, teamsDataUpdatedAt]);

  // Client-side hooks for pagination and search
  const { paginationState, setPaginationState, resetPageNumber } = usePagination();

  const { searchTerm, setSearchTerm, filteredData: searchFilteredSessions } = useSearch<SessionDetails>(
    sessions || [],
    {
      searchBy: (session: SessionDetails) => {
        const values: string[] = [];
        if (session?.name) values.push(session.name);
        if (session?.vendor_name) values.push(session.vendor_name);
        if (session?.created_by) values.push(session.created_by);
        return values;
      },
      resetPageNumber,
    }
  );

  // Calculate pagination values from filtered data
  const totalSessions = searchFilteredSessions?.length ?? 0;
  const totalPages = Math.ceil(totalSessions / paginationState.rowsPerPage);

  // Client-side pagination (slice the filtered data)
  const paginatedSessions = useMemo(() => {
    const start = (paginationState.currentPage - 1) * paginationState.rowsPerPage;
    const end = paginationState.currentPage * paginationState.rowsPerPage;
    return searchFilteredSessions?.slice(start, end) ?? [];
  }, [searchFilteredSessions, paginationState.currentPage, paginationState.rowsPerPage]);

  // Handle opening the modal
  const handleNewAssessment = () => {
    setUploadDialogState({
      isOpen: true,
      type: AGENT_TYPES.MULTI_FILE_VENDOR_ASSESSMENT,
    });
  };

  /**
   * Load session data into store
   */
  const loadSessionResponseDataIntoStore = useCallback(
    async (session: MultiVendorAgentSession, steps: AgentSessionStep[]) => {
      try {
        await handleContinueMultiVendorAssessment({
          session,
          steps,
        });
      } catch (error) {
        console.error(error);
        addNotification({
          type: "error",
          title: "Error",
          message: "Failed to load session data. Please try again.",
        });
      }
    },
    []
  );

  /**
   * Continue/View session handler
   */
  const handleContinueSession = useCallback(
    async (session: SessionDetails) => {
      // Prevent duplicate clicks
      if (isLoading || isFetching || loadingRows[session.id]) {
        return;
      }

      setLoadingRows((prev) => ({ ...prev, [session.id]: true }));

      try {
        // Fetch full session with steps
        const response = await getAgentSession(session.id);

        // Load into agent store using existing use-case
        await loadSessionResponseDataIntoStore(
          response.data.session as MultiVendorAgentSession,
          response.data.steps
        );

        // Navigate to agent workflow page
        navigate(`/${ROUTES.AGENT}/${session.id}`);
      } catch (err) {
        console.error("Error loading session:", err);
        addNotification({
          type: "error",
          title: "Error",
          message: "Failed to load session. Please try again.",
        });
      } finally {
        setLoadingRows((prev) => ({ ...prev, [session.id]: false }));
      }
    },
    [navigate, loadSessionResponseDataIntoStore, isLoading, isFetching, loadingRows]
  );

  /**
   * Handle row click to navigate to session
   */
  const handleRowClick = useCallback(
    async ({ row }: { row: SessionDetails }) => {
      await handleContinueSession(row);
    },
    [handleContinueSession]
  );

  /**
   * Download report handler
   */
  const handleDownloadReport = useCallback(async (session: SessionDetails, format?: string) => {
    setLoadingRows((prev) => ({ ...prev, [session.id]: true }));

    try {
      // Fetch session steps for download
      const response = await getAgentSession(session.id);

      const { session: sessionData, steps } = response.data;
      // Load session data into store before generating report
      await parsePromise(loadSessionResponseDataIntoStore(sessionData as MultiVendorAgentSession, steps));
      await generateAndDownloadSessionReport(session.id, format, { steps });
    } catch (err) {
      console.error("Error downloading report:", err);
      addNotification({
        type: "error",
        title: "Download failed",
        message: "Failed to download report. Please try again.",
      });
    } finally {
      setLoadingRows((prev) => ({ ...prev, [session.id]: false }));
    }
  }, [loadSessionResponseDataIntoStore]);

  // Get columns with action handlers
  const columns = getSessionsColumns(loadingRows, handleContinueSession, handleDownloadReport);

  return (
    <ErrorBoundary fallback={null}>
      <div className="w-full h-full flex flex-col">
        {/* Top Bar: Breadcrumb + New Assessment Button */}
        <div className="flex items-center pb-4 justify-between min-h-[44px] max-h-[44px]">
          <div className="flex gap-4 items-center">
            <ZBreadCrumbs
              items={[
                { title: "Third-Party Risk", href: `/${ROUTES.THIRD_PARTY_HOME}` },
                { title: "Sessions", href: `/${ROUTES.THIRD_PARTY_SESSIONS}` },
              ]}
            />
          </div>
          <ZButton color="primary" className="text-xs font-medium h-7 px-3" onClick={handleNewAssessment}>
            New Assessment
          </ZButton>
        </div>

        {/* Main Content */}
        <div className="h-full w-full overflow-hidden rounded-lg flex flex-col">
          {/* Header: Title + Count | Search */}
          <div className="px-6 py-4 flex flex-col bg-white gap-2">
            <div className="flex flex-row justify-between items-center gap-2 flex-shrink-0">
              {/* Left: Title + Count */}
              <div className="flex flex-row items-center gap-2 flex-grow">
                <h2 className="font-['Inter'] font-semibold text-base leading-[20px] text-title-primary">Sessions</h2>
                {!isLoading && !isFetching && (
                  <div className="flex flex-row justify-center items-center py-[1px] px-2 gap-1 bg-badge-bg rounded-[45px]">
                    <span className="font-['Inter'] font-medium text-[10px] leading-[16px] text-badge-text">
                      {totalSessions}
                    </span>
                  </div>
                )}
              </div>

              {/* Right: Search */}
              <div className="flex flex-row items-center gap-2">
                <SearchField
                  value={searchTerm}
                  onChange={setSearchTerm}
                  placeholder="Search by Session Title or Third-Party Name"
                  className="w-[420px]"
                />
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="relative flex-1 min-h-0">
              {/* Loading overlay - only during refetch, not initial load */}
              {!isLoading && isFetching && (
                <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center rounded-lg">
                  <LoadingSpinner />
                </div>
              )}
              <ZTableDeprecrated<SessionDetails>
                className={cn("border-1 border-[#E4E4E7] overflow-auto scroll-auto h-full")}
                layout="auto"
                loading={isLoading || isFetching}
                rows={paginatedSessions}
                columns={columns}
                isHeaderSticky
                isCompact
                removeWrapper
                getRowId={(row) => row.id}
                onRowClick={handleRowClick}
                tableHeaderProps={{ className: "shadow-none rounded-none" }}
                tableRowProps={(row, rowIndex) => ({
                  className: cn(
                    rowIndex === 0 ? "border-none" : "border-y",
                    "group",
                    "bg-white hover:bg-table-row-hover",
                    "cursor-pointer",
                    row && row.id && loadingRows[row.id] && "cursor-wait"
                  ),
                })}
                classNames={{
                  wrapper: "!shadow-none !rounded-none bg-white",
                  base: "!shadow-none !rounded-none bg-white",
                  tbody: "bg-white",
                  tr: "bg-white",
                  th: "!shadow-none !rounded-none !bg-table-header-bg text-table-header-text font-semibold text-xs",
                  td: "text-table-row-text",
                }}
                aria-label="Sessions List"
                tableBodyProps={{
                  emptyContent: isError ? (
                    <div className="flex flex-col items-center justify-center py-8 gap-2">
                      <p className="text-sm text-gray-500">Failed to load sessions</p>
                      {error && <p className="text-xs text-red-500">{error.message}</p>}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center py-8">
                      {isLoading || isFetching ? (
                        <LoadingSpinner containerClassName="bg-transparent relative" className="mx-auto" />
                      ) : (
                        <p className="text-sm text-gray-500">No sessions found</p>
                      )}
                    </div>
                  ),
                }}
              />
            </div>
            <ZTablePagination
              totalPages={totalPages}
              paginationState={paginationState}
              setPagination={setPaginationState}
              className="px-5 !py-3 border-t border-[#E4E4E7]"
            />
          </div>
        </div>
      </div>

      {/* Upload Dialog */}
      <UploadDialog data={uploadDialogState} setIsOpen={setUploadDialogState} />
    </ErrorBoundary>
  );
}
